import React, { useState, useRef, useEffect } from 'react'
import { classNames } from '~/utils/client/helpers'
import { changeLocale } from '~/utils/i18n.client'
import { useLocale } from '~/utils/client/hooks/useLocale'
import { GermanFlag, EnglishFlag, FrenchFlag } from '../Icons/Flags'
import { ChevronDown } from 'lucide-react'
import { i18n } from '@lingui/core'

const languages = [
  { code: 'de', name: 'Deutsch', flag: GermanFlag },
  { code: 'en', name: 'English', flag: EnglishFlag },
  { code: 'fr', name: 'Français', flag: FrenchFlag },
]

export function LanguageSelector({ className }: { className?: string }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const locale = useLocale()

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const currentLanguage = languages.find((lang) => lang.code === locale) || languages[0]

  const handleLanguageChange = (localeCode: string) => {
    if (localeCode !== locale) {
      changeLocale(localeCode)
    }
    setIsOpen(false)
  }

  return (
    <div
      className={classNames('relative bg-surface-hover rounded-md', className)}
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm text-body hover:text-accent transition-colors"
        aria-label={i18n._('Sprache auswählen, {language}', {
          language: currentLanguage.name,
        })}
        aria-expanded={isOpen}
      >
        <currentLanguage.flag className="w-5 h-3" aria-hidden="true" />
        <span className="hidden sm:inline text-heading">{currentLanguage.name}</span>
        <ChevronDown
          className={classNames(
            'w-4 h-4 transition-transform text-heading',
            isOpen && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="absolute bottom-full right-0 mb-2 w-48 bg-white border border-border-default rounded-md shadow-lg z-50">
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={() => handleLanguageChange(language.code)}
              className={classNames(
                'w-full flex items-center space-x-3 px-4 py-3 text-left text-sm hover:bg-surface-inset transition-colors',
                language.code === locale && 'bg-surface-inset font-medium'
              )}
              aria-label={
                language.code === locale
                  ? i18n._('{language}, ausgewählt', { language: language.name })
                  : language.name
              }
            >
              <language.flag className="w-5 h-3" aria-hidden="true" />
              <span className="text-black">{language.name}</span>
              {language.code === locale && (
                <svg
                  className="w-4 h-4 ml-auto text-accent"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
